import { FC, useCallback, useState } from 'react';
import { useSetAtom } from 'jotai';
import { CloneButton as CloneButtonControl } from 'components/clone-button';
import {
  addNotificationsAtom,
  NotificationGroup,
} from 'components/atomised-notifications/atoms';
import { cloneScenarioAtom } from './atoms';

interface CloneButtonProps {
  id: number;
  onCloned: () => void;
}

/**
 * Row-level "Clone scenario" action. Owns its own in-flight state so the
 * containing columns array stays referentially stable across clone clicks
 * (no per-click re-creation). The shared clone atom is fire-and-forget here;
 * we surface progress per row via local state instead of subscribing to the
 * atom's loadable.
 *
 * Renders the shared components/clone-button control, so this row reads the
 * same as the labelled button on the scenario forms. It used to be a bare Copy
 * icon whose meaning was discoverable only by hovering.
 * @param {CloneButtonProps} props - Row id + post-clone callback
 * @return {JSX.Element} The shared clone control, wired to this row
 */
const CloneButton: FC<CloneButtonProps> = ({ id, onCloned }) => {
  const triggerClone = useSetAtom(cloneScenarioAtom);
  const addNotifications = useSetAtom(addNotificationsAtom);
  const [isCloning, setIsCloning] = useState(false);

  const handleClick = useCallback(() => {
    setIsCloning(true);
    triggerClone(id)
      .then(onCloned)
      .catch(() => {
        addNotifications(NotificationGroup.ExpenseScenarios, [
          {
            id: `clone-error-${id}-${Date.now()}`,
            title: 'Clone failed',
            message: 'Could not clone this expense scenario. Please try again.',
            kind: 'error',
            autoHideMs: 5000,
          },
        ]);
      })
      .finally(() => setIsCloning(false));
  }, [addNotifications, id, onCloned, triggerClone]);

  return (
    <CloneButtonControl
      kind="ghost"
      size="sm"
      busy={isCloning}
      onClick={handleClick}
    />
  );
};

export { CloneButton };
